import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { getFeatureFlag } from "@/plugins/posthog";

import { PROFILE_SWIPING_FLAG, PROFILE_DIRECTION_SWIPING_FLAG } from "@/constants";

export const useFeatureFlagsStore = defineStore("featureFlags", () => {
  const flags = ref({});
  const fetchedFlags = ref(new Set());

  const getFlag = computed(() => (flagName) => flags.value[flagName]);

  const isFlagFetched = computed(
    () => (flagName) => fetchedFlags.value.has(flagName)
  );

  const isAnyFlagFetched = computed(
    () => (flagNames) => flagNames.some((name) => fetchedFlags.value.has(name))
  );

  const setFlag = (flagName, value) => {
    flags.value[flagName] = value;
    fetchedFlags.value.add(flagName);
  };

  const fetchFlag = async (flagName) => {
    if (fetchedFlags.value.has(flagName)) {
      return flags.value[flagName];
    }

    try {
      const value = await getFeatureFlag(flagName);
      setFlag(flagName, value);
      return value;
    } catch (error) {
      console.error(`Failed to fetch feature flag ${flagName}:`, error);
      setFlag(flagName, null);
      return null;
    }
  };

  const fetchFlags = async (flagNames) => {
    const promises = flagNames
      .filter((name) => !fetchedFlags.value.has(name))
      .map((name) => fetchFlag(name));

    return Promise.allSettled(promises);
  };

  const resetFeatureFlags = () => {
    flags.value = {};
    fetchedFlags.value = new Set();
  };

  const isSwipingEnabled = computed(() => {
    const variant = flags.value[PROFILE_SWIPING_FLAG];
    return variant === "swipe";
  });

  const isVerticalSwipingEnabled = computed(() => {
    return flags.value[PROFILE_DIRECTION_SWIPING_FLAG] === "vertical";
  });

  return {
    flags,
    fetchedFlags,
    getFlag,
    isFlagFetched,
    isAnyFlagFetched,
    setFlag,
    fetchFlag,
    fetchFlags,
    resetFeatureFlags,
    isSwipingEnabled,
    isVerticalSwipingEnabled,
  };
});
